import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { BlacklistImportScope } from "@/enum/blacklist.enum";
import { useUploadBlacklistMutation } from "@/pages/Private/redux/blacklist/blacklist.api";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { BlacklistUploadResult, UploadBlacklist, UploadBlacklistSchema } from "../schema/blacklist";

/** Pull a human-readable message out of an RTK Query error, falling back to a generic one. */
const apiErrorMessage = (error: unknown): string | null => {
	const data = (error as { data?: { message?: string | string[] } })?.data;
	const message = Array.isArray(data?.message) ? data?.message[0] : data?.message;

	return typeof message === "string" && message ? message : null;
};

const errorStatus = (error: unknown): number | undefined => (error as { status?: number })?.status;

export interface UploadBlacklistCsvState {
	file: File | null;
	/** The file was rejected up front, or the request failed — shown inline in the modal. */
	error: string | null;
	/** Import summary of the last completed upload; kept when nothing could be imported. */
	result: BlacklistUploadResult | null;
	isLoading: boolean;
	/** What to take from the file: only the people, only the companies, or both. */
	scope: BlacklistImportScope;
	selectScope: (scope: BlacklistImportScope) => void;
	selectFile: (file: File | File[]) => void;
	rejectFile: (reason: "type" | "size") => void;
	submit: () => void;
}

/**
 * Owns the blacklist CSV upload: file selection, validation, the request, and how its outcome
 * is surfaced. Both upload modals (Blacklist page and Contacts page) use it, so a fix lands in
 * both instead of drifting between two near-identical copies.
 */
export const useUploadBlacklistCsv = ({
	isGlobal,
	onImported,
}: {
	isGlobal: boolean;
	onImported: () => void;
}): UploadBlacklistCsvState => {
	const { t } = useTranslation();
	const tu = (key: string, options?: Record<string, unknown>) =>
		t(`blacklist.upload.${key}`, options);

	const [uploadBlacklist, { isLoading }] = useUploadBlacklistMutation();
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<BlacklistUploadResult | null>(null);

	const {
		handleSubmit,
		setValue,
		watch,
		clearErrors,
		formState: { errors },
	} = useForm<UploadBlacklist>({
		resolver: zodResolver(UploadBlacklistSchema),
		// Both is what the importer did before the choice existed, so nobody's habit breaks.
		defaultValues: { scope: BlacklistImportScope.BOTH },
	});

	const file = watch("file") ?? null;
	const scope = watch("scope") ?? BlacklistImportScope.BOTH;

	const selectScope = (picked: BlacklistImportScope) => {
		setResult(null);
		setValue("scope", picked, { shouldValidate: true });
	};

	const selectFile = (picked: File | File[]) => {
		setError(null);
		setResult(null);
		clearErrors("file");
		setValue("file", Array.isArray(picked) ? picked[0] : picked, { shouldValidate: true });
	};

	const rejectFile = (reason: "type" | "size") => {
		setResult(null);
		setError(tu(reason === "type" ? "typeError" : "sizeError"));
	};

	const onSubmit = async (values: UploadBlacklist) => {
		setError(null);
		setResult(null);

		const formData = new FormData();

		formData.append("file", values.file, values.file.name);
		formData.append("type", "csv");
		formData.append("isGlobal", String(isGlobal));
		formData.append("scope", values.scope);

		try {
			const summary = await uploadBlacklist(formData).unwrap();

			setResult(summary);

			if (summary.imported > 0) {
				showGlobalToast(tu("importedToast", { count: summary.imported }), 6000, "success");
				onImported();

				return;
			}

			// Nothing landed — keep the modal open so the user can read why.
			setError(tu("nothingImported"));
		} catch (err) {
			const message =
				errorStatus(err) === 403 ? tu("forbidden") : (apiErrorMessage(err) ?? tu("uploadFailed"));

			setError(message);
			showGlobalToast(message);
		}
	};

	return {
		file,
		error: error ?? (errors.file ? tu("fileRequired") : null),
		result,
		isLoading,
		scope,
		selectScope,
		selectFile,
		rejectFile,
		submit: handleSubmit(onSubmit),
	};
};
